import {
  AccessTime,
  Event,
  Folder,
  Image,
  ImageAspectRatio,
  Layers,
  Movie,
  Link as MuiLink,
  PhotoSizeSelectActual,
  Sell,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useUpdateMediaItem } from '~/api/media-items';
import {
  DetailAccordion,
  DetailAccordionContainer,
  DetailAccordionDetails,
  DetailAccordionSummary,
} from '~/components/DetailAccordion';
import { DetailSection } from '~/components/DetailSection';
import { Tooltip } from '~/components/Tooltip';
import { DetailSwitch } from '~/components/forms/details';
import { Link } from '~/components/link';
import { TagList } from '~/components/tag';
import type { MediaKind } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { useLocation } from '~/hooks/location';
import { formatDate } from '~/lib/datetime';
import { VIDEO_LIKE_KINDS, mediaDimensions, mediaExtension, mediaSize } from '~/lib/media';
import { formatRuntime } from '~/lib/runtime';
import { formatBytes } from '~/lib/units';
import type { MediaItemInfoProps__MediaItem } from './MediaItemInfo.generated';

// Kinds with no servable original, so "use original" can't apply. MediaItem
// validates the same rule.
const NO_ORIGINAL_KINDS: readonly MediaKind[] = ['PDF', 'POWERPOINT'];

/* GraphQL */ `#graphql
fragment MediaItemInfoProps__MediaItem on MediaItem {
  createdAt
  defaultDuration {
    ...Interval
  }
  folder {
    id
    name
  }
  id
  kind
  originalFile {
    id
    originalFilename
    uri
  }
  originalMetadata
  overlay {
    id
  }
  name
  tags
  thumbnails {
    boundsHeight
    boundsWidth
    uri
  }
  updatedAt
  useOriginal
}
`;

export interface MediaItemInfoProps {
  displayAppLink?: boolean;
  displayTags?: boolean;
  displayUseOriginal?: boolean;
  mediaItem: MediaItemInfoProps__MediaItem;
}

export const MediaItemInfo = ({
  displayAppLink = false,
  displayTags = false,
  displayUseOriginal = false,
  mediaItem,
}: MediaItemInfoProps) => {
  const { t } = useTranslation();
  const [updateMediaItem] = useUpdateMediaItem();

  const link = useLink();
  const location = useLocation();

  // Neither original is servable: a pdf's is the pdf, a powerpoint's is the
  // deck. Both are delivered by rendition.
  const noOriginal = NO_ORIGINAL_KINDS.includes(mediaItem.kind);
  const noOriginalTooltip = noOriginal
    ? t(`components.media_item_info.misc.use_original_disabled.${mediaItem.kind.toLowerCase()}`)
    : '';

  return (
    <DetailAccordionContainer>
      <DetailAccordion defaultExpanded>
        <DetailAccordionSummary>
          {t('components.media_item_info.info.heading')}
        </DetailAccordionSummary>
        <DetailAccordionDetails>
          {displayAppLink ? (
            <DetailSection
              icon={<MuiLink />}
              title={t('components.media_item_info.info.media')}
              value={
                <Link
                  to={link(`/content/${mediaItem.folder.id}/media/${mediaItem.id}/details`)}
                  state={{ returnTo: location.fullPath }}
                >
                  {mediaItem.name}
                </Link>
              }
            />
          ) : (
            <DetailSection
              icon={<MuiLink />}
              title={t('components.media_item_info.info.file')}
              value={
                <Link to={mediaItem.originalFile.uri} target="_blank">
                  {mediaItem.originalFile.originalFilename}
                </Link>
              }
            />
          )}
          <DetailSection
            icon={mediaItem.kind === 'IMAGE' ? <Image /> : <Movie />}
            title={t('components.media_item_info.info.format')}
            value={mediaExtension(mediaItem)}
          />
          <DetailSection
            icon={<Folder />}
            title={t('components.media_item_info.info.folder')}
            value={
              // There's no reason not to link to the root folder here
              // And this check is bad
              mediaItem.folder.name === 'No Folder' ? (
                t('components.media_item_info.info.no_folder')
              ) : (
                <Link to={link(`/content/${mediaItem.folder.id}`)}>{mediaItem.folder.name}</Link>
              )
            }
          />
          <DetailSection
            icon={<Event />}
            title={t('common.labels.created')}
            value={formatDate(mediaItem.createdAt)}
          />
          <DetailSection
            icon={<Event />}
            title={t('components.media_item_info.info.updated')}
            value={formatDate(mediaItem.updatedAt)}
          />
        </DetailAccordionDetails>
      </DetailAccordion>

      <DetailAccordion defaultExpanded>
        <DetailAccordionSummary>
          {t('components.media_item_info.specs.heading')}
        </DetailAccordionSummary>
        <DetailAccordionDetails>
          <DetailSection
            icon="Kb"
            title={t('common.labels.size')}
            value={formatBytes(mediaSize(mediaItem))}
          />
          <DetailSection
            icon={<ImageAspectRatio />}
            title={t('components.media_item_info.specs.dimensions')}
            value={mediaDimensions(mediaItem).join('x')}
          />
          {VIDEO_LIKE_KINDS.includes(mediaItem.kind) && (
            <DetailSection
              icon={<AccessTime />}
              title={t('common.labels.duration')}
              value={formatRuntime(mediaItem.defaultDuration, 3)}
            />
          )}
        </DetailAccordionDetails>
      </DetailAccordion>

      {(displayTags || displayUseOriginal) && (
        <DetailAccordion defaultExpanded>
          <DetailAccordionSummary>
            {t('components.media_item_info.misc.heading')}
          </DetailAccordionSummary>
          <DetailAccordionDetails>
            {displayUseOriginal && (
              <DetailSection
                icon={<PhotoSizeSelectActual />}
                title={t('components.media_item_info.misc.use_original')}
                value={
                  <Tooltip title={noOriginalTooltip}>
                    <span>
                      <DetailSwitch
                        defaultChecked={mediaItem.useOriginal}
                        inputProps={{
                          'aria-label': t('components.media_item_info.misc.use_original'),
                        }}
                        disabled={noOriginal}
                        onChange={(event) =>
                          void updateMediaItem({
                            variables: {
                              mediaItemId: mediaItem.id,
                              patch: { useOriginal: event.target.checked },
                            },
                          })
                        }
                        size="small"
                      />
                    </span>
                  </Tooltip>
                }
              />
            )}
            {displayTags && (
              <DetailSection
                icon={<Sell />}
                title={t('common.labels.tags')}
                value={
                  mediaItem.tags.length ? (
                    <TagList
                      item={mediaItem}
                      sx={{ justifyContent: 'flex-end' }}
                      tags={mediaItem.tags}
                    />
                  ) : (
                    '--'
                  )
                }
              />
            )}
            {mediaItem.overlay && (
              <DetailSection
                icon={<Layers />}
                title={t('components.media_item_info.misc.overlay_id')}
                value={mediaItem.overlay.id}
              />
            )}
          </DetailAccordionDetails>
        </DetailAccordion>
      )}
    </DetailAccordionContainer>
  );
};
